import {
  BonusStrategy,
  BonusType,
  BonusResult,
  ConfettiConfig,
  AmountConstraints,
} from '../interfaces/bonus.interface';
import { IndiceArrepentimientoFinanciero } from '@utils/indece-arrepentimiento-financiero';

/**
 * Strategy: Bono porcentual "Índice Arrepentimiento Financiero 2026" (0.2%)
 * - Activo del 31/01/2026 con query params específicos
 * - Monto mínimo: $50,000
 * - Aplica para cualquier plazo (sin términos bloqueados)
 * - Términos y condiciones: reutiliza la URL del bono porcentual
 */
export class Iaf2026BonusStrategy implements BonusStrategy {
  type = BonusType.IAF_2026;
  campaignSpecific = true; // Específico de campaña IAF

  constructor(public iafService: IndiceArrepentimientoFinanciero | null = null) {}

  validateAmount(amount: number, minAmount: number): boolean {
    return amount >= minAmount;
  }

  private isWithinActiveWindow(startAt: Date, endAt: Date, now: Date = new Date()): boolean {
    return now >= startAt && now <= endAt;
  }

  calculateBonus(amount: number, termDays: number): BonusResult {
    const terms = this.getTermsAndConditions();
    const iafData = this.iafService?.IAF();
    const cfg = iafData && iafData.active && iafData.bonus_blue_days ? iafData.bonus_blue_days : null;

    if (!cfg || !iafData?.active) {
      return {
        isValid: false,
        bonusAmount: 0,
        message: '',
        confettiType: '',
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description,
      };
    }

    const percentageDecimal = (cfg.percentage || 0.2) / 100;
    const minAmount: number = typeof cfg.min_amount === 'number' ? cfg.min_amount : 50000;
    const startAt: Date = cfg.start_at ? new Date(cfg.start_at) : new Date('2026-01-31T00:00:00');
    const endAt: Date = cfg.end_at ? new Date(cfg.end_at) : new Date('2026-01-31T23:59:59');
    const label: string = cfg.label || '+0.20%';

    if (!this.isWithinActiveWindow(startAt, endAt) || !this.validateAmount(amount, minAmount)) {
      return {
        isValid: false,
        bonusAmount: 0,
        message: '',
        confettiType: '',
        termsAndConditionsUrl: terms.url,
        termsAndConditionsDescription: terms.description,
      };
    }

    const bonusAmount = Math.round(amount * percentageDecimal * (termDays / 360));
    const displayAmount = this.formatCurrency(bonusAmount);

    return {
      isValid: true,
      label,
      value: label,
      bonusAmount,
      messages: {
        desktop: 'Invierte desde $50.000 COP y MejorCDT te regala un bono extra en tu cuenta bancaria por el Índice de Arrepentimiento Financiero.',
        mobile: 'Invierte desde $50.000 COP y recibe un bono extra por el IAF una vez tu CDT esté activo',
      },
      message_simulador: `Puedes aplicar para un bono de ${displayAmount} por el Índice de Arrepentimiento Financiero. Continúa con <b>la apertura de tu CDT</b> para obtenerlo.`,
      confettiType: 'snow',
      displayAmount,
      bonusTag: cfg.type || 'porcentual',
      minTermDays: cfg.terms_locked,
      tagNameBonusType: cfg.tag_name || 'IAF_2026_PORCENTUAL_020',
      termsAndConditionsUrl: cfg.terms_url || terms.url,
      termsAndConditionsDescription: terms.description,
    };
  }

  public getTermsAndConditions(): { url: string; description: string } {
    return {
      url: 'legal/bono/indice-arrepentimiento-financiero-2026',
      description: 'El bono Índice de Arrepentimiento Financiero es un cashback del 0.2% sobre el valor de tu inversión, válido únicamente para el 31 de enero de 2026 y sujeto a un monto mínimo de $50,000 COP. No acumulable con otras promociones.'
    };
  }

  getConfettiConfig(): ConfettiConfig {
    return {
      type: 'snow',
      duration: 4000,
      intensity: 'medium',
    };
  }

  getAmountConstraints(): AmountConstraints {
    const iafData = this.iafService?.IAF();
    const minAmount = iafData?.bonus_blue_days?.min_amount || 50000;
    
    return {
      locked: false,
      minAmount: minAmount,
    };
  }

  private formatCurrency(amount: number): string {
    return `$${amount.toLocaleString('es-CO')}`;
  }
}
